<template>
  <div
    class="relative flex"
    :class="isLoyaltyDashboardVariant ? 'h-full gap-4 items-start' : 'gap-2 py-4 md:pl-4'"
    :style="theme ? `--theme: ${theme}` : null"
  >
    <!-- basecamp2026 / UTMB mobile: entire row clickable overlay -->
    <div
      v-if="isBasecamp2026SharedVariant && !isLoyaltyDashboardVariant"
      :aria-label="isPastEvent ? $t.eventRecap : $t.viewEvent"
      class="absolute-0 z-20 md:hidden"
      role="button"
      tabindex="0"
      @click="$emit('show-event')"
      @keydown.enter.prevent="$emit('show-event')"
      @keydown.space.prevent="$emit('show-event')"
    />
    <base-button
      v-if="isBasecamp2026SharedVariant && !isLoyaltyDashboardVariant"
      :aria-label="isPastEvent ? $t.eventRecap : $t.viewEvent"
      class="absolute right-2 top-1/2 z-10 md:hidden -translate-y-1/2"
      @click="$emit('show-event')"
    >
      <vf-icon name="chevron" size="md" />
    </base-button>
    <figure
      v-style="isLoyaltyDashboardVariant
        ? { h: { sm: '6.5rem', md: '8.8125rem' }, w: { sm: '6.5rem', md: '8.8125rem' } }
        : { h: { md: '8.8125rem' } }"
      class="relative shrink-0"
      :class="{
        'self-center': isSummitRunClubVariant,
        '<md:hidden': !isLoyaltyDashboardVariant,
        'overflow-hidden rounded': isLoyaltyDashboardVariant,
      }"
    >
      <base-picture
        v-style="isLoyaltyDashboardVariant
          ? { h: { sm: '6.5rem', md: '8.8125rem' }, w: { sm: '6.5rem', md: '8.8125rem' } }
          : { h: { md: '8.8125rem' }, w: { md: '8.8125rem' } }"
        :alt="image.alt"
        :class="{
          'op-20': isPastEvent && isUTMBVariant,
          'block h-full w-full rounded': isLoyaltyDashboardVariant,
        }"
        :fit="isLoyaltyDashboardVariant ? 'cover' : undefined"
        :props-img="isLoyaltyDashboardVariant ? { class: 'w-full' } : undefined"
        :src="image.src"
      />
      <div v-if="isPastEvent && isBasecamp2026Variant" class="absolute-0 z-1 backdrop-blur-md" />
      <div
        v-if="isBasecamp2026SharedVariant
          && !isLoyaltyDashboardVariant
          && (props.srcIcon?.src || props.srcIcon?.icon)
          && !isPastEvent"
        class="pointer-events-none absolute-0 z-10 flex center"
      >
        <base-picture
          v-if="props.srcIcon?.src"
          :alt="props.srcIcon.alt || props.srcIcon.name"
          fit="contain"
          height="85"
          :src="props.srcIcon.src"
          width="85"
        />
        <vf-icon
          v-else-if="props.srcIcon?.icon"
          :name="props.srcIcon.icon"
          size="5xl"
        />
      </div>
      <figcaption v-if="label" class="absolute right-0 top-0 min-w-8 flex center bg-$theme p-2 text-xs">
        {{ label }}
      </figcaption>
      <div
        v-if="isPastEvent && isBasecamp2026SharedVariant && !isLoyaltyDashboardVariant"
        class="absolute inset-x-0 top-1/2 z-10 p-2 text-center c-white -translate-y-1/2"
      >
        <div
          v-if="isBasecamp2026Variant"
          class="f-col items-center gap-2"
        >
          <vf-icon name="info" size="md" />
          <span class="caption-1 fw-bold uppercase">{{ $t.eventEnded }}</span>
        </div>
        <span
          v-else
          v-style="{ 'text-size': { sm: '4.26vw', md: pastEventLabelMdFontSize }, 'text-wrap': 'balance' }"
          class="absolute-0 flex center fw-bold lh-4 uppercase"
        >
          {{ $t.eventEnded }}
        </span>
      </div>
    </figure>
    <div v-if="isLoyaltyDashboardVariant" class="h-full min-w-0 f-col flex flex-1">
      <div>
        <template v-if="startDate">
          <p class="text-xs font-secondary">
            <span :class="{ 'line-through c-grey-40': isPastEvent }">
              {{ dateRangeLabel }}
            </span>
          </p>
        </template>
        <p v-else class="text-xs uppercase">
          {{ $t.comingSoon }}
        </p>
        <p v-if="location" class="pb-2 text-xs font-secondary uppercase">
          {{ location }}
        </p>
      </div>
      <div class="mt-a">
        <div class="pb-2 text-md fw-bold">
          <p v-if="$feature.showTitleHtml" v-html="titleHtml" />
          <span v-else>{{ title }}</span>
        </div>
        <base-button class="text-sm underlined" @click="$emit('show-event')">
          {{ $t.discoverEvent }}
        </base-button>
      </div>
    </div>
    <div
      v-else
      class="relative w-full flex justify-center md:f-col"
      :class="{
        'between': isSummitRunClubVariant,
        '<md:items-center': !isBasecamp2026SharedVariant,
        'b-$theme': !isBasecamp2026SharedVariant,
        'md:b-r': !isSummitRunClubVariant && !isBasecamp2026SharedVariant,
      }"
    >
      <div
        class="flex pb-3 md:f-col"
        :class="{
          'f-col': isSummitRunClubVariant,
          'grow': !isSummitRunClubVariant && !isBasecamp2026SharedVariant,
          'flex-none <md:w-full <md:f-col md:between': isBasecamp2026SharedVariant,
        }"
      >
        <div
          class="text-xs"
          :class="{
            'text-sm': isSummitRunClubVariant,
            'w-18 b-r b-grey-70 items-center gap-1 pr-2 md:w-a md:flex':
              !isSummitRunClubVariant && !isBasecamp2026SharedVariant,
            'md:f-col md:items-start md:gap-0 md:pr-0': isBasecamp2026SharedVariant,
          }"
        >
          <template v-if="startDate">
            <vf-icon class="<md:hidden" :class="{ hidden: isSummitRunClubVariant || isBasecamp2026SharedVariant }" name="calendar" size="xs" style="margin-top: -0.125rem" />
            <!-- basecamp2026 / UTMB: inline date with event ended on mobile -->
            <template v-if="isBasecamp2026SharedVariant">
              <p
                class="flex items-center gap-2 font-secondary wrap"
                :class="[
                  { 'c-brand-7': isUTMBVariant && !isPastEvent },
                  isBasecamp2026Variant || isLoyaltyDashboardVariant ? 'text-xs' : 'text-sm',
                ]"
              >
                <span :class="isPastEvent ? isLoyaltyDashboardVariant ? 'line-through c-grey-40' : 'line-through c-grey-60' : ''">
                  {{ dateRangeLabel }}
                </span>
                <span
                  v-if="isPastEvent && !isBasecamp2026Variant && !isLoyaltyDashboardVariant"
                  class="shrink-0 ws-nowrap fw-bold uppercase md:hidden"
                >
                  {{ $t.eventEnded }}
                </span>
              </p>
            </template>
            <template v-else>
              <p>
                {{ startDate }}
                <template v-if="endDate && startDate !== endDate">
                  {{ " - " }}
                  {{ endDate }}
                </template>
              </p>
            </template>
          </template>
          <p v-else class="uppercase">
            {{ $t.comingSoon }}
          </p>
          <p
            v-if="location && isBasecamp2026SharedVariant"
            :class="[
              {
                'flex items-center gap-2 font-secondary wrap': isBasecamp2026Variant || isLoyaltyDashboardVariant,
                'c-grey-90': isPastEvent && isBasecamp2026Variant,
                'uppercase': isLoyaltyDashboardVariant,
              },
              isBasecamp2026Variant || isLoyaltyDashboardVariant ? 'text-xs' : 'text-sm',
              isBasecamp2026Variant
                ? '<md:pb-3'
                : isLoyaltyDashboardVariant
                  ? 'pb-2'
                  : isUTMBVariant ? 'pb-0 c-brand-7 font-secondary' : '',
            ]"
          >
            <span>{{ location }}</span>
            <span
              v-if="isPastEvent && isBasecamp2026Variant"
              class="shrink-0 ws-nowrap c-white fw-bold uppercase md:hidden"
            >
              {{ $t.eventEnded }}
            </span>
          </p>
        </div>
        <template v-if="isBasecamp2026SharedVariant">
          <div class="md:min-h-0 md:f-col md:flex md:flex-1 md:between">
            <div
              :class="isBasecamp2026Variant || isLoyaltyDashboardVariant
                ? 'md:pt-3'
                : isUTMBVariant ? 'md:pt-1' : ''"
            >
              <p
                v-if="!isLoyaltyDashboardVariant && (hasXplrPass || (eventType?.label && !isUTMBVariant))"
                class="flex items-center gap-4"
                :class="[
                  isBasecamp2026Variant ? 'body-2' : 'text-sm',
                  { 'c-grey-60': isPastEvent && isBasecamp2026Variant },
                ]"
              >
                <span v-if="eventType?.label && !isUTMBVariant">{{ eventType.label }}</span>
                <template v-if="hasXplrPass">
                  <span class="c-$theme fw-bold" :style="{ '--theme': '#93A1F6' }">
                    {{ $t.xplrPassExclusive }}
                  </span>
                </template>
              </p>
              <div class="<md:fw-bold">
                <p
                  :class="[
                    isBasecamp2026Variant || isLoyaltyDashboardVariant
                      ? 'text-md fw-bold pb-2'
                      : 'text-md c-white fw-bold',
                    { 'c-grey-60': isPastEvent && isBasecamp2026Variant },
                  ]"
                >
                  <span v-if="$feature.showTitleHtml" v-html="titleHtml" />
                  <span v-else>{{ title }}</span>
                </p>
              </div>
            </div>
            <div class="md:pt-2">
              <base-button
                v-if="isLoyaltyDashboardVariant"
                class="text-sm underlined"
                @click="$emit('show-event')"
              >
                {{ $t.discoverEvent }}
              </base-button>
              <vf-button
                v-else
                class="md:mt-0 md:h-10 md:self-start md:rounded-sm md:px-3 <md:hidden"
                :size="{ md: 'tiny' }"
                :variant="{ md: 'tertiary-inverse' }"
                @click="$emit('show-event')"
              >
                <span class="absolute-0 z-1" />
                <span v-text="isPastEvent ? $t.eventRecap : $t.viewEvent" class="<md:sr-only" />
                <vf-icon class="md:hidden" name="chevron" size="md" />
              </vf-button>
            </div>
          </div>
        </template>
        <template v-else>
          <p v-if="location" class="py-1.5 text-sm">
            {{ location }}
          </p>
          <div :class="isSummitRunClubVariant ? 'my-4' : '<md:pl-2'">
            <p
              :class="[
                isSummitRunClubVariant
                  ? 'text-lg c-white pb-2 lh-5 <md:pt-2 md:text-2xl'
                  : 'fw-bold line-clamp-2 text-sm',
              ]"
            >
              <span v-if="$feature.showTitleHtml" v-html="titleHtml" />
              <span v-else>{{ title }}</span>
            </p>
          </div>
        </template>
      </div>
      <vf-button
        v-if="!isBasecamp2026SharedVariant"
        class="<md:absolute <md:right-0 <md:mr-2 md:self-start"
        :size="{ md: 'tiny' }"
        :variant="{ md: isSummitRunClubVariant ? 'tertiary-inverse' : 'tertiary' }"
        @click="$emit('show-event')"
      >
        <span class="absolute-0 z-1" />
        <span v-text="$t.viewEvent" class="<md:sr-only" />
        <vf-icon class="md:hidden" name="chevron" size="md" />
      </vf-button>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { EventCard } from '#types/components/vf/event-card'

const props = defineProps<EventCard>()

defineEmits<{ 'show-event': [] }>()

const { $feature, $locale, $t } = useNuxtApp()
const isUTMBVariant = computed(() => props?.variant === 'UTMB')
const isBasecamp2026Variant = computed(() => props?.variant === 'basecamp-2026')
const isLoyaltyDashboardVariant = computed(() => props?.variant === 'loyalty-dashboard-event')
const isBasecamp2026SharedVariant = computed(() =>
  isBasecamp2026Variant.value || isUTMBVariant.value || isLoyaltyDashboardVariant.value
)
const isPastEvent = computed(() => props.isPastEvent ?? false)
const isSummitRunClubVariant = computed(() => props?.variant === 'summit-running-club')
const hasXplrPass = computed(() =>
  $feature.enableLoyaltyFeatures
  && Boolean(props.communityEvent)
)
const pastEventLabel = computed(() => ($t.eventEnded ?? ''))
const pastEventLabelMdFontSize = computed(() => pastEventLabel.value.length > 16 ? '1rem' : 'clamp(0px,5.11vw,1.5rem)')

const formatEventDate = (date?: string) => {
  if (!date) return null

  if (isLoyaltyDashboardVariant.value) {
    const [month, day] = dateFormat(date, 'YYYY-MM-DD', 'MM/DD/YYYY').split('/')
    return `${Number(month)}/${Number(day)}`
  }

  if (isUTMBVariant.value) {
    const full = dateFormat(date, 'YYYY-MM-DD', 'DD/MM/YYYY')
    return full.replace(/\/\d{4}$/, '')
  }

  return isBasecamp2026SharedVariant.value
    ? dateFormat(date, 'YYYY-MM-DD', 'DD/MM/YYYY')
    : new Date(date).toLocaleDateString($locale)
}

const startDate = computed(() => formatEventDate(props.date?.start))
const endDate = computed(() => formatEventDate(props.date?.end))

const formatTime = computed(() => {
  if (!isUTMBVariant.value) return null
  const start = props.date?.startTime
  const end = props.date?.endTime
  if (!start) return null

  const normalizedStart = start.trim().toLowerCase()
  const normalizedEnd = (end ?? '').trim().toLowerCase()
  const isAllDay = normalizedStart === normalizedEnd

  if (isAllDay) return $t.allDay
  if (!end) return start
  return formatTimeRange(start, end).replace(/\s/g, '')
})

const dateRangeLabel = computed(() => {
  if (!startDate.value) return null

  if (isLoyaltyDashboardVariant.value) {
    const yearSource = props.date?.end || props.date?.start || ''
    const year = yearSource
      ? dateFormat(yearSource, 'YYYY-MM-DD', 'YYYY-MM-DD').slice(0, 4)
      : ''
    const range = endDate.value && startDate.value !== endDate.value
      ? `${startDate.value} - ${endDate.value}`
      : startDate.value
    return year ? `${range}, ${year}` : range
  }

  const range = endDate.value && startDate.value !== endDate.value
    ? `${startDate.value}${isUTMBVariant.value ? '-' : ' - '}${endDate.value}`
    : startDate.value
  if (isUTMBVariant.value && formatTime.value) return `${range} ${formatTime.value}`
  return range
})
</script>
